import { colors } from '@packages/core/styles';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import React from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    layout: {
        display: 'grid',
        gridTemplateAreas: `
            'header header'
            'content sidebar'
        `,
        gridTemplateColumns: 'minmax(0, 1fr) 400px',
        gridTemplateRows: 'auto 1fr',
        columnGap: 24,
        rowGap: 16,
        alignItems: 'start',
        minHeight: '100%',
    },
    header: {
        gridArea: 'header',
        backgroundColor: colors.white,
        zIndex: 2,
    },
    headerSticky: {
        position: 'sticky',
        top: 0,
    },
    content: {
        gridArea: 'content',
        minWidth: 0,
    },
    sidebar: {
        gridArea: 'sidebar',
        alignSelf: 'stretch',
        backgroundColor: colors.grayOne,
        borderLeft: `1px solid ${colors.grayThree}`,
    },
    sidebarInner: {
        padding: 16,
    },
    sidebarSticky: {
        position: 'sticky',
        top: 16,
    },
    '@media (max-width: 992px)': {
        layout: {
            gridTemplateAreas: `
                'header'
                'content'
                'sidebar'
            `,
            gridTemplateColumns: '1fr',
        },
        sidebar: {
            borderLeft: 'none',
            borderTop: `1px solid ${colors.grayThree}`,
        },
        headerSticky: {
            position: 'static',
        },
        sidebarSticky: {
            position: 'static',
        },
    },
});

/**
 * Props for the WorksheetLayout component.
 */
export type WorksheetLayoutProps = {
    /** Main content rendered in the left column. */
    children?: React.ReactNode;

    /** Additional CSS class applied to the root grid container. */
    className?: string;

    /** Header content spanning both columns. */
    header?: React.ReactNode;

    /** Whether to show skeleton loaders instead of content. */
    loading?: boolean;

    /** Sidebar content rendered in the right column. */
    sidebar?: React.ReactNode;

    /** Whether the header sticks to the top of the scroll container. Defaults to true. */
    stickyHeader?: boolean;

    /** Whether the sidebar sticks to the top of the scroll container. Defaults to true. */
    stickySidebar?: boolean;
};

export const WorksheetLayout: React.FC<WorksheetLayoutProps> = React.memo(
    ({ header, sidebar, children, loading = false, stickyHeader = true, stickySidebar = true, className = '' }) => {
        const classes = useStyles();

        const headerClass = `${classes.header} ${stickyHeader ? classes.headerSticky : ''}`;
        const sidebarClass = `${classes.sidebar} ${stickySidebar ? classes.sidebarSticky : ''}`;

        return (
            <div className={`${classes.layout} ${className}`} data-testid="worksheet-layout">
                <div className={headerClass} data-testid="worksheet-layout-header">
                    {loading ? (
                        <div className="d-flex align-items-center gap-2">
                            <SkeletonLoader type="icon" />
                            <SkeletonLoader type="text" width={220} />
                            <div className="ms-auto">
                                <SkeletonLoader type="badge" width={120} />
                            </div>
                        </div>
                    ) : (
                        header || null
                    )}
                </div>

                <main className={classes.content} data-testid="worksheet-layout-content">
                    {loading ? (
                        <>
                            <SkeletonLoader type="text" />
                            <SkeletonLoader type="text" />
                            <SkeletonLoader type="text" />
                            <SkeletonLoader type="text" />
                        </>
                    ) : (
                        children
                    )}
                </main>

                <aside className={sidebarClass} data-testid="worksheet-layout-sidebar">
                    <div className={classes.sidebarInner}>
                        {loading ? (
                            <>
                                <SkeletonLoader type="text" />
                                <SkeletonLoader type="text" />
                                <SkeletonLoader type="text" />
                                <SkeletonLoader type="btn" />
                            </>
                        ) : (
                            sidebar || null
                        )}
                    </div>
                </aside>
            </div>
        );
    }
);
